import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Mail, Briefcase, FileDown, RefreshCw, Inbox, UserPlus, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function OwnerInbox({ onInternAdded }) {
  const [tab, setTab] = useState("applications");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const urls = { applications: "/owner/applications", messages: "/owner/messages", enquiries: "/owner/enquiries" };
      const { data } = await api.get(urls[tab]);
      setItems(data);
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const convertToIntern = async (it) => {
    setConverting(it.id);
    try {
      const { data } = await api.post(`/owner/applications/${it.id}/convert`);
      const cid = data.intern.credential_id;
      if (data.already) toast.info(`Already added to interns · ${cid}`);
      else toast.success(`Added to intern list · ${cid}`);
      setItems((s) => s.map((x) => x.id === it.id
        ? { ...x, converted_intern_id: data.intern.id, converted_credential_id: cid, read: true }
        : x));
      onInternAdded?.();
    } catch (e) {
      toast.error("Could not add intern");
    } finally {
      setConverting(null);
    }
  };

  const markRead = async (it) => {
    if (it.read) return;
    const urls = { applications: "/owner/applications", messages: "/owner/messages", enquiries: "/owner/enquiries" };
    await api.patch(`${urls[tab]}/${it.id}/read`);
    setItems((s) => s.map((x) => x.id === it.id ? { ...x, read: true } : x));
  };

  const downloadResume = (it) => {
    fetch(`${API}/owner/applications/${it.id}/resume`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${localStorage.getItem('vj_token') || ''}` },
    }).then(async (r) => {
      if (!r.ok) return;
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = it.original_resume_name || `resume-${it.id}`;
      document.body.appendChild(link); link.click(); link.remove();
    });
  };

  return (
    <section className="px-6 lg:px-12 py-12 bg-[#0B1120]" data-testid="owner-inbox">
      <div className="max-w-[1400px]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline text-slate-400 flex items-center gap-2"><Inbox size={12}/> Owner Inbox</div>
            <h2 className="font-display text-2xl font-extrabold text-white tracking-tight mt-1">Applications, Enquiries & Messages</h2>
          </div>
          <button className="vj-btn-ghost-dark" onClick={load} data-testid="inbox-refresh"><RefreshCw size={14}/> Refresh</button>
        </div>

        <div className="inline-flex border border-slate-700 mb-5">
          <TabBtn active={tab==="applications"} onClick={() => setTab("applications")} icon={<Briefcase size={13}/>} testid="tab-applications">
            Applications {items.length && tab==="applications" ? `· ${items.length}` : ""}
          </TabBtn>
          <TabBtn active={tab==="enquiries"} onClick={() => setTab("enquiries")} icon={<Sparkles size={13}/>} testid="tab-enquiries">
            Enquiries {items.length && tab==="enquiries" ? `· ${items.length}` : ""}
          </TabBtn>
          <TabBtn active={tab==="messages"} onClick={() => setTab("messages")} icon={<Mail size={13}/>} testid="tab-messages">
            Messages {items.length && tab==="messages" ? `· ${items.length}` : ""}
          </TabBtn>
        </div>

        <div className="vj-card-dark overflow-hidden">
          {loading && <div className="p-8 text-center text-slate-400">Loading…</div>}
          {!loading && items.length === 0 && <div className="p-8 text-center text-slate-500">No {tab} yet.</div>}
          {!loading && items.map((it) => (
            <div key={it.id} className="border-b border-slate-800 last:border-0">
              <button onClick={() => { setOpen(open === it.id ? null : it.id); markRead(it); }} className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-slate-900/50" data-testid={`inbox-row-${it.id}`}>
                <span className={`mt-1.5 h-2 w-2 rounded-full ${it.read ? "bg-slate-600" : "bg-blue-400"}`}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-semibold text-white truncate">{it.name}</div>
                    <div className="text-[11px] font-mono text-slate-500">{(it.created_at || "").slice(0,10)}</div>
                  </div>
                  <div className="text-sm text-slate-400 truncate">{it.email}</div>
                  <div className="text-sm text-slate-300 mt-1">
                    {tab === "applications"
                      ? `${it.application_type} · ${it.role}`
                      : tab === "enquiries"
                      ? `Enquiry · ${it.technology || "Custom Enquiry"}`
                      : (it.message || "").slice(0, 90)}
                  </div>
                </div>
              </button>
              {open === it.id && (
                <div className="px-5 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {tab === "applications" && (
                    <>
                      <KV k="Institute / Company" v={it.institute}/>
                      <KV k="Phone" v={it.phone}/>
                      <KV k="Role" v={it.role}/>
                      <KV k="Type" v={it.application_type}/>
                      <KV k="Preferred Mode" v={it.preferred_mode}/>
                      <div className="md:col-span-2"><div className="overline text-slate-400">Statement</div><div className="text-slate-300 mt-1 whitespace-pre-wrap">{it.message || "—"}</div></div>
                      {it.resume_filename && (
                        <div className="md:col-span-2">
                          <button onClick={() => downloadResume(it)} className="vj-btn-ghost-dark" data-testid={`download-resume-${it.id}`}>
                            <FileDown size={14}/> Download Resume · {it.original_resume_name}
                          </button>
                        </div>
                      )}
                      <div className="md:col-span-2 pt-1 border-t border-slate-800 mt-1">
                        {it.converted_credential_id ? (
                          <span className="inline-flex items-center gap-2 text-sm text-emerald-400 font-semibold" data-testid={`converted-badge-${it.id}`}>
                            <CheckCircle2 size={15}/> Added to intern list · {it.converted_credential_id}
                          </span>
                        ) : (
                          <button
                            onClick={() => convertToIntern(it)}
                            disabled={converting === it.id}
                            className="vj-btn-primary"
                            data-testid={`convert-intern-${it.id}`}
                          >
                            {converting === it.id ? <Loader2 size={14} className="animate-spin"/> : <UserPlus size={14}/>}
                            Select as Intern
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {tab === "enquiries" && (
                    <>
                      <KV k="Phone" v={it.phone}/>
                      <KV k="Technology / Interest" v={it.technology || "Custom Enquiry"}/>
                      <div className="md:col-span-2"><div className="overline text-slate-400">Enquiry</div><div className="text-slate-300 mt-1 whitespace-pre-wrap">{it.message}</div></div>
                    </>
                  )}
                  {tab === "messages" && (
                    <>
                      <KV k="Phone" v={it.phone}/>
                      <div className="md:col-span-2"><div className="overline text-slate-400">Message</div><div className="text-slate-300 mt-1 whitespace-pre-wrap">{it.message}</div></div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TabBtn({ active, onClick, icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 ${active ? "bg-[#1E3A8A] text-white" : "bg-transparent text-slate-300 hover:bg-slate-900"}`}>
      {icon} {children}
    </button>
  );
}

function KV({ k, v }) {
  return (
    <div><div className="overline text-slate-400">{k}</div><div className="text-slate-200 mt-1">{v || "—"}</div></div>
  );
}
